import React from 'react'
import { capitalize, lowerCase } from 'lodash'
import { useTranslation } from 'react-i18next'

import { OptionItem } from '@src/components/CheckoutModal/BasketSummary/BasketItems/BasketItem/OptionItem'
import { FulfilmentWhenLabel } from '@src/components/FulfilmentWhenLabel/FulfilmentWhenLabel'
import { FulfilmentWhereLabelSavedAddress } from '@src/components/FulfilmentWhereLabel/FulfilmentWhereLabelSavedAddress'
import { FulfilmentWhereLabelTable } from '@src/components/FulfilmentWhereLabel/FulfilmentWhereLabelTable'
import { DotSeparatedList } from '@src/components/DotSpan/DotSeparatedList'
import { NarrowFulfilmentMethodInputType } from '@src/graphql-types'
import {
  OutletFulfilmentStateType,
  useOutletFulfilment,
} from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/useOutletFulfilment'
import { useFormatCurrency } from '@src/hooks/useFormatCurrency'
import { UseBasketTotalsResponseData } from '@src/hooks/useTotals/useBasketTotals'
import { useBasketTotalsContext } from '@src/hooks/useTotals/BasketTotalsContext'
import { AsyncDataWrapper } from '@src/hooks/types'

import {
  Container,
  CouponContainer,
  FulfilmentContainer,
  Header,
  HiddenRow,
  MainContainer,
  OptionItemContainer,
  Row,
  RowContainer,
  SpaceBetweenRow,
  StyledCouponContainer,
  SubHeader,
  Table,
  TableData,
  PaddedDotSpan,
  Divider,
  StyledBasketBoostSVG,
} from './OrderSummary.styles'
import { useTheme } from 'styled-components'
import { CheckoutRewards } from '@src/components/CustomerLoyaltyCard/CheckoutRewards/CheckoutRewards'

export const OrderSummary: React.FC<{
  addressId?: string
  newAddress?: string
  tableId?: string
  basketTotals?: AsyncDataWrapper<UseBasketTotalsResponseData>
}> = ({
  addressId = '',
  tableId = '',
  newAddress = '',
  basketTotals: basketTotalsProp,
}) => {
  const { t } = useTranslation('checkout')
  const { basketTotals: contextBasketTotals } = useBasketTotalsContext()

  const theme = useTheme()
  const formatCurrency = useFormatCurrency()
  const {
    data: {
      currentFulfilment: { narrowType: currentNarrowFulfilmentMethod },
    },
    outlet,
  } = useOutletFulfilment({
    stateType: OutletFulfilmentStateType.GLOBAL,
  })
  // Note: Prefer prop over context, no fallback hook needed since we're in checkout context
  const basketTotalsData = basketTotalsProp || contextBasketTotals
  const basketTotals = basketTotalsData.data

  if (!basketTotals) return null

  const { __typename, id, city, countryCode, ...address } = outlet.outletAddress

  const collectionAddress = Object.values(address)
    .filter(value => value && value !== '')
    .join(', ')

  const showRewards =
    (basketTotals?.checkoutBasketTotals?.loyaltyCards?.length ?? 0) > 0 ||
    basketTotals?.checkoutBasketTotals?.appliedDiscount ||
    basketTotals?.checkoutBasketTotals?.bestPotentialDiscount ||
    basketTotals?.checkoutBasketTotals?.addOnItemsTotal ||
    (basketTotals?.checkoutBasketTotals?.minimumSpendToEarnOffers ?? 0) > 0

  return (
    <Container>
      <SpaceBetweenRow>
        <Header>{t('order_summary.order_summary')}</Header>
        <SubHeader>
          <FulfilmentContainer>
            <DotSeparatedList dotSpan={<PaddedDotSpan>{'•'}</PaddedDotSpan>}>
              {[
                currentNarrowFulfilmentMethod &&
                  capitalize(t(lowerCase(currentNarrowFulfilmentMethod))),

                currentNarrowFulfilmentMethod !==
                  NarrowFulfilmentMethodInputType.TABLE && (
                  <FulfilmentWhenLabel
                    stateType={OutletFulfilmentStateType.GLOBAL}
                  />
                ),

                currentNarrowFulfilmentMethod ===
                  NarrowFulfilmentMethodInputType.COLLECTION &&
                  collectionAddress,

                currentNarrowFulfilmentMethod ===
                  NarrowFulfilmentMethodInputType.DELIVERY && (
                  <FulfilmentWhereLabelSavedAddress
                    addressId={addressId}
                    newAddress={newAddress}
                  />
                ),

                currentNarrowFulfilmentMethod ===
                  NarrowFulfilmentMethodInputType.TABLE &&
                  !outlet.isPendingTableNumbersEnabled && (
                    <FulfilmentWhereLabelTable
                      tableId={tableId}
                      outletId={outlet.id}
                    />
                  ),
              ]}
            </DotSeparatedList>
          </FulfilmentContainer>
        </SubHeader>
      </SpaceBetweenRow>
      <MainContainer>
        <Table>
          <HiddenRow>
            <th>{t('order_summary.item')}</th>
            <th>{t('order_summary.cost')}</th>
          </HiddenRow>
          {basketTotals.basketItemsWithPrices.map(item => (
            <RowContainer key={item.basketItem.id}>
              <Row>
                <TableData>{`${item.basketItem.quantity}x ${item.outletMenuItem.name}`}</TableData>
                <TableData>
                  {item.isAddOnItem ? (
                    <CouponContainer>
                      <StyledCouponContainer>
                        <StyledBasketBoostSVG
                          fill={theme.colors.brand}
                          id="offer-item"
                          title={t('order_summary.offer_item')}
                          desc={t('order_summary.offer_item_desc')}
                        />
                      </StyledCouponContainer>
                      {/*  TODO show old price stikethrough here ?? */}
                      {formatCurrency(item.totalPrice)}
                    </CouponContainer>
                  ) : (
                    <>{formatCurrency(item.totalPrice)}</>
                  )}
                </TableData>
              </Row>
              {item.selectedOptionItems.length > 0 && (
                <OptionItemContainer>
                  {item.selectedOptionItems.map(optionItem => (
                    <OptionItem
                      optionItem={optionItem}
                      key={optionItem.id}
                      isOrderSummary
                      quantity={item.basketItem.quantity}
                    />
                  ))}
                </OptionItemContainer>
              )}
            </RowContainer>
          ))}
        </Table>
        {/* Only show divider and CheckoutRewards if there are rewards or discounts to display */}
        {showRewards && (
          <>
            <Divider />
            <CheckoutRewards
              basketSubTotal={
                basketTotals?.checkoutBasketTotals?.nonAddOnBasketItemsTotal ??
                0
              }
              outletId={outlet.id}
              appliedDiscount={
                basketTotals?.checkoutBasketTotals?.appliedDiscount?.discount ||
                null
              }
            />
          </>
        )}
      </MainContainer>
    </Container>
  )
}
